import { Inject, Injectable } from "@angular/core";
import { Http, L10n, QueryBus } from "@skylab-angular-shared/core";
import { ToastService } from "@skylab-angular-shared/ui";
import { KsefEnvironment, KsefEnvironmentQuery } from "@skylab-shared/business";
import { HttpService, L10nBase, TaggedUrl, Tags } from "@skylab-shared/core";
import { Info, KsefServiceEnvironment, KsefVisualPreviewScriptDocumentParameters, RegisterHighlightStyles, RenderPreviewFunc } from "./document-ksef-xml-visual-preview.types";

type KsefPreviewWindow = Window & {
	ksefPreviewModule?: {
		renderPreview: RenderPreviewFunc
		info: Info;
		registerHighlightStyles: RegisterHighlightStyles;
	}
};

export type KsefVisualPreviewParameters = Partial<Omit<KsefVisualPreviewScriptDocumentParameters, 'ksefServiceEnvironment'>>;

@Injectable({providedIn: 'root'})
export class DocumentKsefXmlVisualPreviewService {
	private plainScript!: string;
	private environment!: KsefEnvironment;

	public constructor(
		@Inject(Http) private readonly http: HttpService,
		@Inject(L10n) private readonly l10n: L10nBase,
		private readonly queryBus: QueryBus,
		private readonly toastService: ToastService
	) { }

	public async renderPreviewInNewWindowAsync(xml: string | undefined, parameters: KsefVisualPreviewParameters, title?: string): Promise<void> {
		try {
			const targetParameters = parameters ? { ...parameters } : {} as KsefVisualPreviewScriptDocumentParameters;

			if (!xml) throw new Error('XML file not exist');

			await this.loadPlainScriptAsync();
			await this.loadEnvironment();

			const newWindow = window.open('', '_blank') as KsefPreviewWindow | null;
			if (!newWindow) throw new Error('Could not open new preview window');
			newWindow.document.title = targetParameters.ksefNumber || title || '';

			const container = newWindow.document.createElement("div");
			newWindow.document.body.appendChild(container);

			await this.bindPlainScriptToWindowAsync(newWindow);

			const invoiceVerificationHash = targetParameters.invoiceVerificationHash || '';
			const certificateVerificationUrl = targetParameters.certificateVerificationUrl || '';
			const ksefDate = targetParameters.ksefDate || '';

			if (newWindow.ksefPreviewModule) {
				newWindow.ksefPreviewModule.renderPreview(
					container,
					xml,
					{
						...targetParameters,
						ksefServiceEnvironment: this.scriptEnvironment,
						invoiceVerificationHash,
						certificateVerificationUrl,
						ksefDate
					} as any
				);
			}
		} catch (err) {
			this.toastService.showAsync({
				type: 'error',
				text: this.l10n._T('scenes.shared.document-ksef-visual-preview.error')
			});
		}
	}

	public get scriptEnvironment(): KsefServiceEnvironment {
		return (<Record<KsefEnvironment, KsefServiceEnvironment>>{
			['Demo']: 'demo',
			['Prod']: 'prod',
			['Test']: 'test',
		})[this.environment];
	}

	private async loadEnvironment(): Promise<void> {
		if (this.environment) return;

		const { environment } = await this.queryBus.queryAsync(new KsefEnvironmentQuery());
		this.environment = environment;
	}

	private async loadPlainScriptAsync(): Promise<void> {
		if (this.plainScript) return;

		const url = TaggedUrl.join('eInvoices/visualScript', [Tags.Api, Tags.NotHandle5xx]);
		const { data } = await this.http.get(url, {
			transformResponse: []
		});

		if (!data?.length) return;

		this.plainScript = `data:text/javascript;charset=utf-8,${encodeURIComponent(`${data}`)}`;
	}


	private async bindPlainScriptToWindowAsync(window: Window): Promise<void> {
		const loadedEventName = 'ksef_visual_script_loaded';

		return new Promise(resolve => {
			const scriptElement = window.document.createElement('script');
			scriptElement.type = 'text/javascript';

			window.addEventListener(loadedEventName, () => {
				resolve();
			});

			scriptElement.text = `
				( async () => {
					const ev = new CustomEvent(\`${loadedEventName}\`);
					const ksefPreviewModule = await import(\`${this.plainScript}\`);
					window.ksefPreviewModule = ksefPreviewModule;
					window.dispatchEvent(ev);
				})()
			`;
			window.document.body.appendChild(scriptElement);
		});
	}
}
